import * as React from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'

import {
  DeliveryMethod,
  OTPDeliveryMethods,
  SetupCompleteProps,
} from '@ally/transmitigator'
import { Box, Icon, Space, Button, TextBody, upToMd } from '@ally/metronome-ui'
import { deprecated } from '@ally/metronome-icons'
import { getPrivateAttributes } from '@ally/private'
import { LoginHeading } from '@ally/ally-login'

import { useTransmit } from '../../../TransmitContext'
import { formatSMS } from '../../../Utils'

const SuccessContainer = styled(Box)`
  display: grid;
  grid-template-columns: 20px; 1fr;
  grid-gap: 10px;
  grid-auto-rows: auto;
`

const StyledSuccessIcon = styled(Icon)`
  grid-column: 1;
  grid-row: 1;
`

const SuccessIcon: React.FC = () => (
  <StyledSuccessIcon icon={deprecated.muiCheckMark} fill="success" size="lg" />
)

const SuccessMessageContainer = styled(Box)`
  grid-column: 2;
  display: grid;
  row-gap: 5px;
`

const SuccessTextBody = styled(TextBody)`
  color: #0b8100;
`

interface SuccessContentProps {
  className?: string
  title: string
  messages: string[]
  isPrivate?: boolean
}

const SuccessContent: React.FC<SuccessContentProps> = ({
  title,
  messages,
  className,
  isPrivate,
}) => {
  const privateAttrs = isPrivate ? getPrivateAttributes() : {}
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SuccessContainer className={className} {...privateAttrs}>
      <SuccessIcon />
      <SuccessMessageContainer>
        <SuccessTextBody tag="b" size="sm" weight="bold">
          {title}
        </SuccessTextBody>
        {messages.map(message => (
          <TextBody tag="p" size="sm" weight="bold" key={message}>
            {message}
          </TextBody>
        ))}
      </SuccessMessageContainer>
    </SuccessContainer>
  )
}

interface DeliveryOption {
  id: string
  type: DeliveryMethod
  value: string
  objectPos: number
  /* Returns masked value if masked, otherwise value */
  getPossibleMaskedValue: () => string
}

export const getOptionsFromOTPDeliveryMethods = (
  methods: OTPDeliveryMethods,
): DeliveryOption[] => {
  return new Array(6)
    .fill(undefined)
    .reduce((options: DeliveryOption[], _, i) => {
      const offset = (i + 1).toString()
      const kValue = `allyRCotpDeliveryValue${offset}` as keyof OTPDeliveryMethods
      const value = methods[kValue]
      const kMethod = `allyRCotpDeliveryMethod${offset}` as keyof OTPDeliveryMethods
      const type = methods[kMethod]?.toUpperCase() as DeliveryMethod

      if (value && type === 'SMS') {
        options.push({
          id: v4(),
          type: 'SMS',
          objectPos: i,
          value,
          getPossibleMaskedValue: (): string => formatSMS(value),
        })
      }

      return options
    }, [])
    .sort((a, b) => {
      // Sort by last 4 digits of phone number
      return a.value.slice(-4) < b.value.slice(-4) ? -1 : 1
    })
}

export const SetupCompleteContent: React.FC<SetupCompleteProps> = ({
  setupData: {
    deviceRegistration: isDeviceRegistered,
    deliveryOptions,
    passwordHint,
    isEsignAccepted,
  },
  onComplete,
  status,
}) => {
  const { hostServices } = useTransmit()

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Security Setup Complete',
    })
  }, [])
  const formattedDeliveryOptions = React.useMemo(
    () => getOptionsFromOTPDeliveryMethods(deliveryOptions),
    [deliveryOptions],
  )

  const deliveryOptionMessages = formattedDeliveryOptions.map(
    ({ type, getPossibleMaskedValue }) =>
      `${getPossibleMaskedValue()} (${
        type === 'SMS' ? 'text message' : 'email'
      })`,
  )

  return (
    <>
      <LoginHeading title="Setup is Complete" />
      <Space mb="sm">
        <Box>
          <TextBody tag="b" size="sm" weight="bold">
            Here&apos;s what you just set up
          </TextBody>
        </Box>

        <Space ml="md">
          <SuccessContent
            isPrivate
            title="Security Code Delivery Options"
            messages={deliveryOptionMessages}
          />

          <SuccessContent
            title="Computer Registration"
            messages={[
              ` You chose ${isDeviceRegistered ? '' : 'not'} to register this
              computer.`,
            ]}
          />

          {passwordHint && passwordHint.length > 0 ? (
            <SuccessContent
              title="Password and Hint"
              messages={[` Password hint: ${passwordHint}`]}
              isPrivate
            />
          ) : (
            <></>
          )}
          {isEsignAccepted ? (
            <SuccessContent
              title="Online Banking Disclosures"
              messages={[
                'You consented to receive Ally account information electronically',
              ]}
            />
          ) : (
            <></>
          )}
        </Space>
      </Space>

      <Box width={upToMd(12, 9)} my="md">
        <Button
          variant="primary"
          text="Go to My Accounts"
          onClick={onComplete}
          loading={status === 'Loading'}
        />
        <Space my="sm">
          <TextBody size="sm" tag="p">
            <TextBody size="sm" tag="b" weight="bold">
              Want to edit your settings?
            </TextBody>{' '}
            You can change what you set up anytime by logging in and editing
            your profile.
          </TextBody>
        </Space>
      </Box>
    </>
  )
}

export const SetupComplete: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'SetupComplete') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SetupCompleteContent {...activeScreen.payload} />
}
